import { Component, OnInit, signal, inject, computed, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { Column, FieldConfig } from '@sharedComponents/table-dynamic/interfaces';
import { ActionButton } from '@sharedComponents/table-dynamic/interfaces/actions-buttons.interface';
import { DynamicTableConfig } from '@sharedComponents/table-dynamic/interfaces/dynamic-table.interface';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Validators } from '@angular/forms';
import { ButtonConfig } from '@sharedComponents/modal-template/modal-template.component';
import { buildEquipmentFormPayload } from '../../utils/equipment-form.config';
import { MatDialog } from '@angular/material/dialog';
import { OnboardingService } from '@servicios/onboarding.service';
import { InventoryQuickGuideComponent } from 'app/_sharedComponents/inventory-quick-guide/inventory-quick-guide.component';

@Component({
  selector: 'app-equipments-list',
  templateUrl: './equipments-list.component.html',
  styleUrls: ['./equipments-list.component.css'],
  standalone: false
})
export class EquipmentsListComponent implements OnInit {
  private inventoryService = inject(InventoryService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private dialog = inject(MatDialog);
  private onboardingService = inject(OnboardingService);
  private tourSubscription?: Subscription;

  stats = signal<any>(null);
  loading = signal(false);
  data = signal<any[]>([]);
  totalRecords = signal(0);
  pageSize = signal(10);
  first = signal(0);
  // Dialog & Form
  showForm = signal(false);
  equipmentFormValue = signal<any | null>(null);
  isEditing = signal(false);
  readonly cols: Column[] = [
    { field: 'equipment_code', header: 'Código', filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
    { field: 'name', header: 'Nombre', filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
    { field: 'brand.name', header: 'Marca', filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
    { field: 'model', header: 'Modelo', filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
    { field: 'serial_number', header: 'N/S', filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
    { field: 'vendor.name', header: 'Proveedor', filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
    {
      field: 'status',
      header: 'Estado',
      pipe: 'equipmentstatus',
      filter: {
        enable: true,
        type: 'select',
        options: [
          { label: 'Activo / Operativo', value: 'ACTIVE' },
          { label: 'Operativo', value: 'OPERATIONAL' },
          { label: 'Fuera de Servicio', value: 'OUT_OF_SERVICE' },
          { label: 'En Mantenimiento', value: 'MAINTENANCE' },
          { label: 'En Calibración', value: 'CALIBRATION' },
          { label: 'Retirado', value: 'WITHDRAWN' }
        ],
        optionLabel: 'label',
        optionValue: 'value',
        matchMode: 'equals',
        showMenu: false
      }
    },
    { field: 'next_maintenance_date', header: 'Próx. Mantenimiento', pipe: 'date' },
    { field: 'next_calibration_date', header: 'Próx. Calibración', pipe: 'date' }
  ];

  tableActions = signal<ActionButton[]>([
    { label: 'Editar', icon: 'pi pi-pencil', type: 'edit', action: (item) => this.openEdit(item) },
    { label: 'Ver Detalles', icon: 'pi pi-calendar', type: 'details', action: (item) => this.onRowDblClick(item) },
    { label: 'Eliminar', icon: 'pi pi-trash', type: 'delete', action: (item) => this.confirmDelete(item) }
  ]);

  // Computed config for table-dynamic
  tableConfig = computed<DynamicTableConfig<any>>(() => ({
    data: this.data(),
    totalRecords: this.totalRecords(),
    first: this.first(),
    pageSize: this.pageSize(),
    columns: this.cols,
    loading: this.loading(),
    lazy: true,
    showPagination: true,
    showFilters: true,
    selectable: false,
    exportable: false,
  }));

  ngOnInit() {
    this.loadStats();
    this.loadData({ rows: this.pageSize(), first: 0 });
    this.startEquipmentTour();
  }

  ngOnDestroy() {
  }

  startEquipmentTour(force = false) {
    this.onboardingService.startTour({
      id: 'equipments-basics',
      steps: [
        {
          selector: '#tour-eq-header',
          title: '🔬 Control de Equipos',
          content: 'Administrá el ciclo de vida completo de tus equipos: alta, mantenimiento, calibración y trazabilidad técnica.',
          position: 'bottom'
        },
        {
          selector: '#tour-eq-stats',
          title: '📊 Indicadores de Estado',
          content: 'Visualizá rápidamente cuántos equipos están operativos, en mantenimiento, con vencimientos próximos y fuera de servicio.',
          position: 'bottom'
        },
        {
          selector: '#tour-eq-table',
          title: '📋 Catálogo de Equipos',
          content: 'La tabla muestra todos los equipos con su código, nombre, marca, modelo, número de serie, proveedor, estado y fechas de próximo mantenimiento y calibración. Aplicá filtros para encontrar lo que necesitás.',
          position: 'top'
        },
        {
          selector: '#tour-eq-help',
          title: '✅ ¡Listo para usar!',
          content: 'Hacé doble clic en cualquier fila para ingresar a la hoja de vida completa del equipo: historial de mantenimientos, calibraciones, bitácora de uso y documentación técnica. Podés reiniciar esta guía con el botón "Ayuda".',
          position: 'bottom'
        }
      ]
    }, force);
  }



  async loadStats() {
    try {
      const res = await this.inventoryService.getEquipmentStatistics();
      this.stats.set(res);
    } catch (error) {
      console.error('Error loading equipment stats:', error);
    }
  }

  async loadData(event: any) {
    this.loading.set(true);
    try {
      const rows = Number(event.rows) || this.pageSize();
      const first = Number(event.first) || 0;
      const page = Math.floor(first / rows) + 1;

      const res = await this.inventoryService.getAll('equipments', {
        page,
        limit: rows,
        search: event.globalFilter || event.filters?.global?.value || '',
        sortBy: event.sortField || 'id',
        sortOrder: (event.sortOrder >= 0 ? event.sortOrder : 1) > 0 ? 'asc' : 'desc',
        filters: event.filters
      });

      this.data.set(res?.data ?? []);
      this.totalRecords.set(res?.total ?? 0);
      this.pageSize.set(rows);
      this.first.set(first);
    } catch (error) {
      console.error('Error loading equipment data:', error);
      this.data.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  // Called when a row is double-clicked
  onRowDblClick(item: any) {
    this.router.navigate(['/equipments', item.id]);
  }

  // Called from the action menu
  handleCustomAction(event: { item: any; actionType: string }) {
    if (event.actionType === 'details') {
      this.router.navigate(['/equipments', event.item.id]);
    }
  }

  openNew() {
    this.equipmentFormValue.set(null);
    this.isEditing.set(false);
    this.showForm.set(true);
  }

  openEdit(item: any) {
    this.equipmentFormValue.set(item);
    this.isEditing.set(true);
    this.showForm.set(true);
  }

  hideDialog() {
    this.showForm.set(false);
    this.equipmentFormValue.set(null);
  }

  async saveEquipment(data: any) {
    this.loading.set(true);
    try {
      if (this.isEditing() && data.id) {
        await this.inventoryService.update('equipments', data.id, data);
        this.messageService.add({ severity: 'success', summary: 'Actualizado', detail: 'Equipo actualizado correctamente' });
      } else {
        const { id, ...payload } = data;
        await this.inventoryService.create('equipments', payload);
        this.messageService.add({ severity: 'success', summary: 'Creado', detail: 'Equipo registrado correctamente' });
      }
      this.hideDialog();
      this.loadStats();
      this.loadData({ rows: this.pageSize(), first: this.first() });
    } catch (e: any) {
      console.error(e);
      const isValidation = e?.status === 409 || e?.status === 400 || e?.status === 422 || (e?.error?.message && e?.status < 500);
      const detail = e?.error?.message || e?.message || 'No se pudo guardar el equipo';
      this.messageService.add({
        severity: isValidation ? 'warn' : 'error',
        summary: isValidation ? 'Atención' : 'Error',
        detail,
        life: 6000
      });
    } finally {
      this.loading.set(false);
    }
  }

  confirmDelete(item: any) {
    this.confirmationService.confirm({
      message: `¿Está seguro de que desea eliminar el equipo ${item.brand?.name || ''} ${item.model || ''} (${item.serial_number})?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.delete(item)
    });
  }

  async delete(item: any) {
    this.loading.set(true);
    try {
      await this.inventoryService.delete('equipments', item.id, item.v_);
      this.messageService.add({ severity: 'success', summary: 'Eliminado', detail: 'Equipo eliminado correctamente' });
      this.loadStats();
      this.loadData({ rows: this.pageSize(), first: this.first() });
    } catch (error: any) {
      console.error(error);
      const isValidation = error?.status === 409 || error?.status === 400 || error?.status === 422 || (error?.error?.message && error?.status < 500);
      const detail = error?.error?.message || error?.message || 'No se pudo eliminar el equipo';
      this.messageService.add({
        severity: isValidation ? 'warn' : 'error',
        summary: isValidation ? 'Atención' : 'Error',
        detail,
        life: 6000
      });
    } finally {
      this.loading.set(false);
    }
  }

  openGuide() {
    this.startEquipmentTour(true);
  }
}
